import { Injectable } from '@angular/core';

import {
  CorporateOrganization,
  AccountStatus,
  AccountPlatform,
  SortableAccount,
  SortableAccountGroup
} from '../../../domain/account.domain';

@Injectable({
  providedIn: 'root'
})
export class AccountsSortingService {

  private static readonly ACCOUNT_DESCRIPTION_CODE_SORT_ORDER: Map<AccountPlatform, number> =
    new Map<AccountPlatform, number>([
      [AccountPlatform.DIRECT_INVESTMENT, 0], // FIRST!
      [AccountPlatform.DIRECT_INVESTMENT_LITE, 0],
      [AccountPlatform.DIRECT_INVESTMENT_THINKORSWIM, 0],
      [AccountPlatform.DIRECT_INVESTMENT_ACTIVE_TRADER, 0],
      [AccountPlatform.FINANCIAL_PLANNING, 1],
      [AccountPlatform.PRIVATE_INVESTMENT_ADVICE, 2],
      [AccountPlatform.PARTNER_SERVICES, 4],
      [AccountPlatform.ASSET_MANAGEMENT, 5],
      [AccountPlatform.INSTITUTIONAL_SERVICES, 6],
      [AccountPlatform.UNKNOWN, 7] // LAST
    ]);

  sort<T extends SortableAccount>(accounts: Array<T>): Array<T> {
    const favoriteGroupNumber = this.getFavoriteGroup(accounts);
    return accounts.sort(this.getComparator(favoriteGroupNumber));
  }

  sortAccountGroups<T extends SortableAccountGroup>(accountGroups: Array<T>): Array<T> {
    return accountGroups.sort(this.accountGroupComparator);
  }

  private getComparator<T extends SortableAccount>(favoriteGroupNumber: string): (account1: T, account2: T) => number {
    return (account1: T, account2: T): number => {
      if (account1.corporateOrganization === CorporateOrganization.RETAIL) {
        if (account2.corporateOrganization === CorporateOrganization.RETAIL) {
          // both accounts are retail accounts, compare by account number
          return this.compareValues<string>(account1.number, account2.number);
        }
        return 1;
      }
      if (account2.corporateOrganization === CorporateOrganization.RETAIL) {
        return -1;
      }
      // both are brokerage accounts, regular sorting
      return this.brokerageAccountsComparator(account1, account2, favoriteGroupNumber);
    };
  }

  private accountGroupComparator = (accountGroup1: SortableAccountGroup, accountGroup2: SortableAccountGroup): number => {
    return this.brokerageAccountGroupsComparator(accountGroup1, accountGroup2);
  };

  private getFavoriteGroup<T extends SortableAccount>(accounts: Array<T>): string {
    const favoriteAccount: T = accounts?.find((account: T): boolean => account?.favorite);
    return favoriteAccount !== undefined ? favoriteAccount.groupNumber : undefined;
  }

  private compareValues<T>(first: T, second: T): number {
    if (first === second) {
      return 0;
    }
    if (first < second) {
      return -1;
    }
    return 1;
  }

  private brokerageAccountsComparator<T extends SortableAccount>(account1: T, account2: T, favoriteGroupNumber: string): number {
    let rtn = -this.compareValues<boolean>(
      this.isFavoriteNotClosed(account1, favoriteGroupNumber),
      this.isFavoriteNotClosed(account2, favoriteGroupNumber));
    if (rtn === 0) {
      rtn = this.compareValues<boolean>(account1.status === AccountStatus.CLOSED, account2.status === AccountStatus.CLOSED);
      if (rtn === 0) {
        rtn = -this.compareValues<boolean>(
          this.isFavoriteIgnoreClosed(account1, favoriteGroupNumber),
          this.isFavoriteIgnoreClosed(account2, favoriteGroupNumber));
        if (rtn === 0) {
          rtn = this.compareNickNames(account1, account2);
          if (rtn === 0) {
            rtn = this.compareValues<number>(
              this.getAccountPlatformSortOrder(account1.accountPlatform),
              this.getAccountPlatformSortOrder(account2.accountPlatform)
            );
            if (rtn === 0) {
              rtn = this.localeCompare(account1.groupNumber, account2.groupNumber);
              if (rtn === 0 && account1.number !== undefined) {
                rtn = this.localeCompare(account1.number, account2.number);
              }
            }
          }
        }
      }
    }
    return rtn;
  }

  private brokerageAccountGroupsComparator(accountGroup1: SortableAccountGroup, accountGroup2: SortableAccountGroup): number {
    let rtn = -this.compareValues<boolean>(accountGroup1.favorite, accountGroup2.favorite);
    if (rtn === 0) {
      rtn = this.compareNickNames(accountGroup1, accountGroup2);
      if (rtn === 0) {
        rtn = this.compareValues<number>(
          this.getAccountPlatformSortOrder(accountGroup1.accountPlatform),
          this.getAccountPlatformSortOrder(accountGroup2.accountPlatform)
        );
        if (rtn === 0) {
          rtn = this.localeCompare(accountGroup1.groupNumber, accountGroup2.groupNumber);
        }
      }
    }
    return rtn;
  }

  private compareNickNames<T extends { nickName?: string }>(account1: T, account2: T): number {
    const account1NickNameLength = account1.nickName !== undefined && account1.nickName.length > 0 ? 1 : 0;
    const account2NickNameLength = account2.nickName !== undefined && account2.nickName.length > 0 ? 1 : 0;
    let rtn = account2NickNameLength - account1NickNameLength; // shorter name come last!?! Usually first.
    if (rtn === 0 && ((account1.nickName !== undefined && account1.nickName.length > 0) ||
      (account2.nickName !== undefined && account2.nickName.length > 0))) {
      rtn = this.localeCompare(account1.nickName, account2.nickName);
    }
    return rtn;
  }

  private localeCompare(first: string, second: string): number {
    if (first !== undefined && first !== null) {
      return first.localeCompare(second);
    } else if (second !== undefined && second !== null) {
      return -second.localeCompare(first);
    } else {
      return 0;
    }
  }

  private isFavoriteNotClosed(account: SortableAccount, favoriteGroupNumber: string): boolean {
    return account.favorite || (account.groupNumber === favoriteGroupNumber && account.status !== AccountStatus.CLOSED);
  }

  private isFavoriteIgnoreClosed(account: SortableAccount, favoriteGroupNumber: string): boolean {
    return account.favorite || account.groupNumber === favoriteGroupNumber;
  }

  private getAccountPlatformSortOrder(accountPlatform: AccountPlatform): number {
    return AccountsSortingService.ACCOUNT_DESCRIPTION_CODE_SORT_ORDER.get(accountPlatform);
  }
}
